import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {SwissAppConfigModels} from 'src/app/models/swissappconfig-models';
import {HelperService} from 'src/app/services/helper.service';
import {LocalStorageService} from '../../local-storage.service';
import {Employees, EmpWeekPlan, EmpWorkPlan} from '../models/employees-model';
import {CostCenterObjektRange, CostCenterObjektStatusRange, Kostenstelle} from '../models/kostenstelle-model';
import {Orders} from '../models/order-model';
import {Spesen} from '../models/spesen-model';
import {CostCenter, OrderData, TimeControlEntry,} from '../models/timeControlEntry';
import moment, {Moment} from 'moment';
import {IndexedDbData} from '../models/indexedDB-model';
import {DatumFormatPipe} from 'src/app/pipes/datum-format.pipe';
import {Observable, Subject, tap, timeout} from 'rxjs';
import {ToaterMessagesService} from './toaster-messages.service';
import {UploadImagesService} from './upload-images.service';
import {UserAuthenticationService} from 'src/app/services/user-authentication.service';
import {CaptureImageData} from '../models/captureImage-model';
import {Capacitor} from '@capacitor/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TimeAppSuppliesService} from '../time-app-material/services/time-app-supplies.service';
import {Platform} from '@angular/cdk/platform';

@Injectable({
    providedIn: 'root',
})
export class DataService {
    private platform = inject(Platform);
    public isToday: boolean = false;
    public myCostCenters: number[] = [];
    public myCostCentersByObjektNumber: number[] = [];
    public kostenStelleModel!: Kostenstelle[];
    public dbDataHasLoaded = false;
    private storage: string = 'SwissTimeEntry';
    private dbKostenstelle!: CostCenter[];
    private costCenterObjektrange!: CostCenterObjektRange[];
    private dbOrders!: Orders[];
    private spesen: Spesen[] = [];
    private plannedWorks: EmpWorkPlan[] = [];
    private allEmployees: Employees[] = [];
    private webServiceConfig?: SwissAppConfigModels.WebServiceConfig;
    private IndexedDbData: IndexedDbData;
    private expirationDays!: number;
    private currEmployeeID!: number;
    private currMandator!: string;
    private dataCalendarDateChangedCallback?: () => void;
    private dbDataLoaded: Subject<boolean> = new Subject();
    private currentTimeEntry?: OrderData;
    private chosenDate?: Date | Moment;
    // TODO workaround for recursive dependency injection in constructor
    // refactor supply sync so we dont need to do this workaround in the first place
    // currently the chosenDate is tracked in this service together with everything else basically (besides the timeapp plus)
    // and we need the aforementioned date to correctly create SupplyEntries
    private supplyService = inject(TimeAppSuppliesService);

    /* Ende ehemaliger "cost-center-by-object-id.service.ts" */

    constructor(private localStorage: LocalStorageService,
                private http: HttpClient,
                private userAuthService: UserAuthenticationService,
                private _datumFormatPipe: DatumFormatPipe,
                private toasterMessagesService: ToaterMessagesService,
                private uploadImageService: UploadImagesService,
                private helperService: HelperService) {
        this.helperService.getSwissWebServiceConfig$().pipe(takeUntilDestroyed(), tap((config) => {
            this.webServiceConfig = config;
        })).subscribe();

        this.IndexedDbData = {
            orders: [], costs: [], timeEntry: [], spesen: [], allEmployees: [],
        };

        this.userAuthService.getCurrentUser().subscribe({
            next: (user) => {
                if (user) {
                    this.getAllDataFromWebServices().then((x) => {
                        if (x) {
                            this.IndexedDbData = x;
                            this.setChosenDate(new Date());
                            this.dbDataHasLoaded = true;
                            this.dbDataLoaded.next(true);
                            this.chosenDate = new Date();
                        }
                    });
                }
            }
        });
        this.chosenDate = new Date();
    }

    public async reloadOrdersFromWebservice(): Promise<Orders[] | undefined> {
        const res = await this.getAllOrders();
        if (!res) {
            return undefined;
        }
        this.IndexedDbData.orders = res;
        await this.localStorage.delete('orders', 'SwissTimeEntry', true);
        await this.localStorage.add('orders', this.IndexedDbData.orders, 'SwissTimeEntry', false, true);
        return this.IndexedDbData.orders;
    }

    public getDbDataLoaded(): Subject<boolean> {
        return this.dbDataLoaded;
    }

    getKostensStellen(): Promise<CostCenter[]> {
        return new Promise((resolve) => {
            let url = this.webServiceConfig!.tishUrl;
            const user = this.userAuthService.getCurrentAuthenticatedUser();
            this.http.get<CostCenter[]>(`${url}/GetAllCostCenter?MandantID=${user?.mandatorID}`, {
                headers: {
                    'Content-Type': 'application/json', Authorization: `${this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo)}`,
                },
            }).subscribe({
                next: async (res) => {
                    this.dbKostenstelle = res;
                    await this.localStorage.add('costCenters', this.dbKostenstelle, this.storage);
                    resolve(this.dbKostenstelle);
                }, error: () => {
                    resolve(this.localStorage.get('costCenters', this.storage, true, []) as Promise<CostCenter[]>);
                },
            });
        });
    }

    getAllCostCenterObjektRanges(): Promise<CostCenterObjektRange[]> {
        return new Promise((resolve) => {
            let url = this.webServiceConfig!.tishUrl;
            const user = this.userAuthService.getCurrentAuthenticatedUser()!;
            this.http.get<CostCenterObjektRange[]>(`${url}/GetAllCostCenterObjektRanges?MandantID=${user?.mandatorID}`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `${this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo)}`,
                },
            }).pipe(timeout(this.helperService.isWebServiceAvailable() ? 1000 : 20)).subscribe({
                next: async (res) => {
                    this.costCenterObjektrange = res;
                    await this.localStorage.add('costCenterObjektrange', this.costCenterObjektrange, this.storage);
                    resolve(this.costCenterObjektrange);
                }, error: async () => {
                    this.costCenterObjektrange = await this.localStorage.get('costCenterObjektrange', this.storage, false, []);
                    resolve(this.costCenterObjektrange);
                },
            });
        });
    }

    async getCostCenterByAuftrag(auftrag: Orders, kostenStelle: CostCenter): Promise<CostCenter> {
        if (auftrag && auftrag.objectNumber) {
            this.GetCostCentersByObjectID(auftrag.objectNumber, auftrag.status, this.IndexedDbData.costs).then((res) => {
                let costCentersByObjectID = res;
                if (costCentersByObjectID && costCentersByObjectID.length > 0) {
                    let idx = costCentersByObjectID.findIndex(x => x.costCenterID === kostenStelle.costCenterID);
                    if (idx === -1) {
                        return kostenStelle;
                    } else {
                        kostenStelle.obligatoryWorkplace = costCentersByObjectID[idx].obligatoryWorkplace;
                        return kostenStelle;
                    }
                } else {
                    return kostenStelle;
                }
            });
        }
        return kostenStelle;
    }

    getAllOrders(): Promise<Orders[]> {
        return new Promise((resolve) => {
            let url = this.webServiceConfig!.tishUrl;
            const user = this.userAuthService.getCurrentAuthenticatedUser();
            this.http.get<Orders[]>(`${url}/GetAllOrders?MandantID=${user?.mandatorID}`, {
                headers: {'Content-Type': 'application/json', Authorization: `${this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo)}`}
            }).subscribe({
                next: async (res) => {
                    this.dbOrders = res;
                    this.IndexedDbData.orders = this.dbOrders;
                    await this.localStorage.add('orders', this.dbOrders, this.storage, false, true);
                    resolve(this.dbOrders);
                }, error: () => {
                    resolve(this.localStorage.get('orders', this.storage, true, []) as Promise<Orders[]>);
                },
            });
        });
    }

    getAllEmployee(): Promise<Employees[]> {
        return new Promise((resolve) => {
            const user = this.userAuthService.getCurrentAuthenticatedUser();
            if (user) {
                const url = this.webServiceConfig!.tishUrl;
                this.http.get<Employees[]>(`${url}/GetAllEmployee?MandantID=${user.mandatorID}&EmployeeID=${user.employeeID}`, {
                    headers: {
                        'Content-Type': 'application/json', Authorization: `${this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo)}`,
                    },
                }).subscribe({
                    next: async (res) => {
                        this.allEmployees = res;
                        this.IndexedDbData.allEmployees = this.allEmployees;
                        await this.localStorage.add('allEmployees', this.allEmployees, this.storage);
                        resolve(this.allEmployees);
                    }, error: () => {
                        resolve(this.localStorage.get('allEmployees', this.storage));
                    },
                });
            } else {
                resolve(this.localStorage.get('allEmployees', this.storage));
            }
        });
    }

    getAllSpesen(): Promise<Spesen[]> {
        return new Promise((resolve) => {
            let url = this.webServiceConfig!.tishUrl;
            const user = this.userAuthService.getCurrentAuthenticatedUser()!;
            this.http.get<Spesen[]>(`${url}/GetAllExpenses?MandantID=${user.mandatorID}`, {
                headers: {
                    'Content-Type': 'application/json', Authorization: `${this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo)}`,
                },
            }).subscribe({
                next: (res) => {
                    this.spesen = res;
                    this.IndexedDbData.spesen = this.spesen;
                    this.localStorage.add('spesen', this.spesen, this.storage, false, true);
                    resolve(this.spesen);
                }, error: () => {
                    resolve(this.localStorage.get('spesen', this.storage, true) as Promise<Spesen[]>);
                },
            });
        });
    }

    saveTimeControlEntry(timeControlEntry: TimeControlEntry) {
        let url = this.webServiceConfig!.tishUrl;
        return this.http.post(url + '/UploadTimeControlEntry', timeControlEntry, {
            headers: {
                'Content-Type': 'application/json', Authorization: `${this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo)}`,
            },
        });
    }

    getPlannedEmployeeWorks(): Promise<EmpWorkPlan[]> {
        return new Promise((resolve) => {
            let url = this.webServiceConfig!.tishUrl;
            const user = this.userAuthService.getCurrentAuthenticatedUser()!;
            this.http.get<EmpWorkPlan[]>(`${url}/GetPlannedEmployeeWorks?EmployeeID=${user.employeeID}&MandantID=${user.mandatorID}`, {
                headers: {
                    'Content-Type': 'application/json', Authorization: `${this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo)}`,
                },
            }).subscribe({
                next: async (res) => {
                    this.plannedWorks = res;
                    this.IndexedDbData.plannedEmployeeWorks = this.plannedWorks;

                    const data = await this.localStorage.addToExisting<EmpWorkPlan[]>('plannedEmployeeWorks', this.plannedWorks, this.storage);
                    resolve(data);
                }, error: async () => {
                    resolve(await this.localStorage.get('plannedEmployeeWorks', this.storage));
                },
            });
        });
    }

    getPlannedEmployeeWeeks(): Promise<EmpWeekPlan[]> {
        return new Promise((resolve) => {
            let url = this.webServiceConfig!.tishUrl;
            const user = this.userAuthService.getCurrentAuthenticatedUser()!;
            this.http.get<EmpWeekPlan[]>(`${url}/GetPlannedEmployeeWeeks?EmployeeID=${user.employeeID}&MandantID=${user.mandatorID}`, {
                headers: {
                    'Content-Type': 'application/json', Authorization: `${this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo)}`,
                },
            }).subscribe({
                next: async (res) => {
                    await this.localStorage.add('plannedEmployeeWeeks', res, this.storage);
                    this.IndexedDbData.plannedEmployeeWeeks = res;
                    resolve(res);
                }, error: () => {
                    resolve(this.localStorage.get('plannedEmployeeWeeks', this.storage));
                },
            });
        });
    }

    replaceTimeControlEntryInIndexedDB(timeControlEntry: TimeControlEntry) {
        let index = this.IndexedDbData.timeEntry.findIndex((te) => te.rowID === timeControlEntry.rowID);
        if (index > -1) {
            this.IndexedDbData.timeEntry[index] = timeControlEntry;
        }
    }

    checkDeletedTimeControlEntries(): Promise<Boolean> {
        return new Promise((resolve) => {
            this.getMyTimeControlEntriesWebCall().subscribe({
                next: (res: TimeControlEntry[]) => {
                    // Hier werden alle Positionen abgeholt, welche im Navi vorhanden sind
                    let naviTimeControlEntries: TimeControlEntry[] = res;

                    if (this.IndexedDbData.timeEntry.length > 0 && naviTimeControlEntries.length > 0) {
                        // Die letzten 30 Tage auf gelöschte Positionen prüfen
                        let zeitraumStart = new Date();
                        zeitraumStart.setDate(zeitraumStart.getDate() - 30);
                        zeitraumStart.setHours(0);
                        zeitraumStart.setMinutes(0);

                        this.IndexedDbData.timeEntry
                            .filter((x) => x.exported)
                            .forEach((tce) => {
                                let zeitEintragDatum = new Date(tce.entryDate);
                                if (zeitraumStart < zeitEintragDatum) {
                                    if (naviTimeControlEntries.filter((navi) => navi.rowID === tce.rowID).length === 0) {
                                        // Der Eintrag wurde im Navi nicht gefunden, also auf gelöscht setzen
                                        tce.deleted = true;
                                        this.replaceTimeControlEntryInIndexedDB(tce);
                                    }
                                }
                            });
                        // Eventuell vorhandene Änderungen in der IndexedDB löschen
                        this.refreshTimeEntryInIndexedDB();
                    }

                    resolve(true);
                }, error: (error: any) => {
                    console.error('Fehler beim Abholen den Planned Works. ', error);
                    resolve(false);
                },
            });
        });
    }

    async loadDataFromIndexDB(): Promise<IndexedDbData> {
        this.IndexedDbData = await this.localStorage.getAllDataFromIndexedDB();
        const res2 = await this.localStorage.get('deleteTimeEntry', this.storage);
        this.expirationDays = res2!;

        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        // Aktuelle Mitarbeiter-Id ermitteln
        this.currEmployeeID = user.employeeID!;
        this.currMandator = user.mandatorID!;
        return this.IndexedDbData;
    }

    async checkForExpirationDay(timeEntry: TimeControlEntry): Promise<void> {
        const currDate = moment.now();
        const daysPassed = moment(currDate).diff(timeEntry.entryDate, 'days');

        if (daysPassed > this.expirationDays) {
            const index = this.IndexedDbData.timeEntry.indexOf(timeEntry);
            this.IndexedDbData.timeEntry.splice(index, 1);
            await this.refreshTimeEntryInIndexedDB();
        }
    }

    async refreshTimeEntryInIndexedDB(): Promise<void> {
        await this.localStorage.delete('myTimeControlEntries', this.storage);
        await this.localStorage.add('myTimeControlEntries', this.IndexedDbData.timeEntry, this.storage);
    }

    async getTimeEntriesFromIndexedDB(): Promise<TimeControlEntry[]> {
        return await this.localStorage.get('myTimeControlEntries', this.storage, false, []);
    }

    getIndexedDBData(): IndexedDbData {
        return this.IndexedDbData;
    }

    async startSyncData(chosenDate: Date | undefined): Promise<boolean> {
        // Lokale Daten von der IndexDB-Laden
        await this.loadDataFromIndexDB();
        // Prüfen ob im Navi Zeiten gelöscht wurden, die Bereits exportiert wurden
        await this.checkDeletedTimeControlEntries();
        // Noch nicht übermittelte Zeitpositionen senden
        let syncErrorExists = false;
        let entriesToSend: TimeControlEntry[];

        if (chosenDate) {
            const chosenDMY = {
                day: chosenDate.getDate(), month: chosenDate.getMonth(), year: chosenDate.getFullYear()
            };
            entriesToSend = this.IndexedDbData.timeEntry.filter((el) => {
                const entryDate = new Date(el.entryDate);
                return el.readyForExport && !el.deleted && !el.exported && entryDate.getDate() == chosenDMY.day && entryDate.getMonth() == chosenDMY.month && entryDate.getFullYear() == chosenDMY.year;
            });
        } else {
            entriesToSend = this.IndexedDbData.timeEntry.filter((el) => el.readyForExport && !el.deleted && !el.exported);
        }

        let countEntriesToSend: number = entriesToSend.length;
        let sentEntries: number = 0;

        return new Promise(async (resolve) => {
            await this.supplyService.syncSupplyEntries(chosenDate);

            if (countEntriesToSend > 0) {
                entriesToSend.forEach((timeEntry) => {
                    // Datenbereinigung
                    timeEntry.originalStartTime = timeEntry.originalStartTime !== '' && timeEntry.originalStartTime != null ? moment(timeEntry.originalStartTime).format('yyyy-MM-DDTHH:mm:ssZ') : null;
                    timeEntry.originalEndTime = timeEntry.originalEndTime !== '' && timeEntry.originalEndTime != null ? moment(timeEntry.originalEndTime).format('yyyy-MM-DDTHH:mm:ssZ') : null;
                    timeEntry.startTime = timeEntry.startTime !== '' && timeEntry.startTime != null ? moment(timeEntry.startTime).format('yyyy-MM-DDTHH:mm:ssZ') : null;
                    timeEntry.endTime = timeEntry.endTime !== '' && timeEntry.endTime != null ? moment(timeEntry.endTime).format('yyyy-MM-DDTHH:mm:ssZ') : null;

                    this.saveTimeControlEntry(timeEntry)
                        .subscribe({
                            next: (res) => {
                                if (res) {
                                    timeEntry.exported = true;
                                    this.refreshTimeEntryInIndexedDB();
                                }
                            }, error: (err) => {
                                syncErrorExists = true;
                                console.error('Fehler bei Speichern einer Zeit: ', err);
                            },
                        })
                        .add(async () => {
                            sentEntries += 1;
                            if (sentEntries === countEntriesToSend) {
                                await this.checkForExpirationDay(timeEntry);
                                await this.reloadTimeControlEntries();
                                resolve(syncErrorExists);
                            }
                        });
                    resolve(false);
                });
            } else {
                // Keine Zeiten zum Hochladen verfügbar => es existiert kein Synchronisierungs-Fehler
                await this.reloadTimeControlEntries();
                resolve(false);
            }
        });
    }

    async deleteTimeControlEntry(rowID: string): Promise<boolean> {
        const foundTimeEntry = this.IndexedDbData.timeEntry.find((el) => el.rowID === rowID);

        const index = this.IndexedDbData.timeEntry.indexOf(foundTimeEntry!);
        this.IndexedDbData.timeEntry.splice(index, 1);
        await this.refreshTimeEntryInIndexedDB();
        return true;
    }

    async closeDay(chosenDate: Date): Promise<TimeControlEntry[]> {
        let filterTimeEntryToday: TimeControlEntry[];
        await this.supplyService.readyDataForExport(chosenDate);
        await this.loadDataFromIndexDB();
        filterTimeEntryToday = this.IndexedDbData.timeEntry.filter((el) => new Date(el.entryDate).getDate() === chosenDate.getDate() && new Date(el.entryDate).getMonth() === chosenDate.getMonth());
        filterTimeEntryToday.forEach((el) => {
            el.readyForExport = true;
            if (el.readyForExport) {
                const index = this.IndexedDbData.timeEntry.indexOf(el);
                this.IndexedDbData.timeEntry.splice(index, 1, el);
            }
        });
        this.refreshTimeEntryInIndexedDB();
        let datum: string = this._datumFormatPipe.transformOnlyDate(chosenDate);
        await this.changeTimeControlDayState(datum, true);
        return filterTimeEntryToday;
    }

    canOpenDay(choosedDay: Date): Promise<boolean> {
        return new Promise((resolve) => {
            // Überprüfung, ob ein Tag wieder geöffnet werden kann
            let filterTimeEntryToday = this.IndexedDbData.timeEntry.filter((el) => new Date(el.entryDate).getDate() === choosedDay.getDate() && new Date(el.entryDate).getMonth() === choosedDay.getMonth() && el.exported);

            if (filterTimeEntryToday.length) {
                resolve(false);
            }
            resolve(true);
        });
    }

    openDay(choosedDay: Date): Promise<TimeControlEntry[]> {
        return new Promise(async (resolve) => {
            let filterTimeEntryToday = this.IndexedDbData.timeEntry.filter((el) => new Date(el.entryDate).getDate() === choosedDay.getDate() && new Date(el.entryDate).getMonth() === choosedDay.getMonth() && el.readyForExport);

            if (filterTimeEntryToday.length > 0) {
                filterTimeEntryToday.forEach((te) => {
                    te.readyForExport = false;
                    this.replaceTimeControlEntryInIndexedDB(te);
                });
                this.getAllDataFromWebServices();
            }
            filterTimeEntryToday = this.IndexedDbData.timeEntry.filter((el) => new Date(el.entryDate).getDate() === choosedDay.getDate() && new Date(el.entryDate).getMonth() === choosedDay.getMonth());
            const datum: string = this._datumFormatPipe.transformOnlyDate(choosedDay);
            await this.changeTimeControlDayState(datum, false);
            await this.localStorage.add('myTimeControlEntries', this.IndexedDbData.timeEntry, this.storage);
            resolve(filterTimeEntryToday);
        });
    }

    getAllTimeControlEntries(): Promise<TimeControlEntry[]> {
        return new Promise((resolve) => {
            this.getMyTimeControlEntriesWebCall().subscribe({
                next: async (res) => {
                    const local = await this.localStorage.get<TimeControlEntry[]>('myTimeControlEntries', this.storage, false, []);
                    res.forEach((tce) => {
                        let index = local.findIndex((x) => x.rowID === tce.rowID);
                        if (index === -1) {
                            local.push(tce);
                        } else {
                            local[index].timeAmount = tce.timeAmount;
                            local[index].startTime = tce.startTime;
                            local[index].endTime = tce.endTime;
                            local[index].sortorder = tce.sortorder;
                        }
                    });

                    await this.localStorage.add('myTimeControlEntries', local, this.storage);
                    this.IndexedDbData.timeEntry = local;
                    resolve(local);
                }, error: async () => {
                    resolve(await this.getTimeEntriesFromIndexedDB());
                },
            });
        });
    }

    getMyTimeControlEntriesWebCall(): Observable<TimeControlEntry[]> {
        let url = this.webServiceConfig!.tishUrl;
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        return this.http.get<TimeControlEntry[]>(`${url}/GetMyTimeControlEntries?EmployeeID=${user?.employeeID}&MandantID=${user?.mandatorID}`, {
            headers: {
                'Content-Type': 'application/json', Authorization: `${this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo)}`,
            },
        });
    }

    setTimeEntryDeleteDays() {
        let daysUntilDelete = 30;
        this.localStorage.add('deleteTimeEntry', 30, this.storage).then((res: any) => (daysUntilDelete = res!));
        return daysUntilDelete;
    }

    setChosenDate(datum: Date) {
        this.chosenDate = datum;
        const today = new Date();
        this.isToday = (datum.getFullYear() == today.getFullYear() && datum.getMonth() == today.getMonth() && datum.getDate() == today.getDate());
        if (this.dataCalendarDateChangedCallback) {
            this.dataCalendarDateChangedCallback();
        }
    }

    //TODO replace all of them with getChosenDateSync
    getChosenDateAsPromise(): Promise<Date> {
        return new Promise((resolve) => {
            if (!this.chosenDate) {
                this.chosenDate = new Date();
            } else {
                if (!(this.chosenDate instanceof Date)) {
                    this.chosenDate = this.chosenDate!.toDate();
                }
            }
            resolve(this.chosenDate as Date);
        });
    }

    getChosenDate(): Date {
        if (!this.chosenDate) {
            this.chosenDate = new Date();
        } else {
            //todo remove momentjs
            // dunno who and where a moment is set for date
            if (!(this.chosenDate instanceof Date)) {
                this.chosenDate = this.chosenDate!.toDate();
            }
        }
        return this.chosenDate as Date;
    }


    public getChosenDateFormatted() {
        const d = this.getChosenDate() as Date;
        return this._datumFormatPipe.getWeekdayAndSwissDate(d);
    }

    resetChosenDate() {
        this.chosenDate = new Date();
    }

    saveTimeControlEntryToIndexedDB(timeControlEntry: TimeControlEntry, editTimeEntryRowID: string | undefined, editTimeEntryDate: string | undefined): Promise<boolean> {
        return new Promise((resolve) => {
            let timeEntryExists: TimeControlEntry | undefined;
            if (this.IndexedDbData.timeEntry.length > 0) {
                timeEntryExists = this.IndexedDbData.timeEntry.find((el) => el.rowID === editTimeEntryRowID);
            }

            if (editTimeEntryDate !== undefined) {
                timeControlEntry.entryDate = editTimeEntryDate;
            }
            if (timeEntryExists) {
                const index = this.IndexedDbData.timeEntry.indexOf(timeEntryExists);
                this.IndexedDbData.timeEntry.splice(index, 1, timeControlEntry);
            } else {
                this.IndexedDbData.timeEntry.push(timeControlEntry);
            }

            this.localStorage.add('myTimeControlEntries', this.IndexedDbData.timeEntry, this.storage).then(() => resolve(true));
        });
    }

    async isDayClosed(datum: string = this._datumFormatPipe.transformOnlyDate(this.getChosenDate())): Promise<boolean> {
        return 'lock' === await this.getTimeControlDayState(datum);
    }


    async getTimeControlDayState(datum: string): Promise<string> {
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        const timeControl = await this.localStorage.get('zeitKontrolleTage', this.storage, false, []);
        if (this.currEmployeeID === undefined) {
            this.currEmployeeID = user.employeeID!;
        }
        if (this.currMandator === undefined) {
            this.currMandator = user.mandatorID!;
        }

        let index = timeControl.findIndex((x) => x.employeeID === this.currEmployeeID && x.datum === datum && x.mandator === this.currMandator);
        if (index >= 0) {
            if (timeControl[index].closed) {
                return 'lock';
            } else {
                return 'lock_open';
            }
        } else {
            return 'lock_open';
        }
    }

    async changeTimeControlDayState(datum: string, doClose: boolean): Promise<Boolean> {
        const timeControl = await this.localStorage.get('zeitKontrolleTage', this.storage, false, []);
        let index = timeControl.findIndex((x) => x.datum === datum && x.employeeID === this.currEmployeeID && x.mandator === this.currMandator);
        if (index == -1) {
            timeControl.push({
                datum: datum, closed: doClose, employeeID: this.currEmployeeID, mandator: this.currMandator
            });
            await this.localStorage.add('zeitKontrolleTage', timeControl, this.storage);
            return true;
        }

        if (timeControl[index].closed != doClose) {
            timeControl[index].closed = doClose;
            await this.localStorage.add('zeitKontrolleTage', timeControl, this.storage);
            return true;
        }
        return false;
    }

    isInThePast(date: Date) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return date < today;
    }

    public async syncImages(): Promise<boolean> {
        const indexedDBImages: CaptureImageData[] = await this.localStorage.get<CaptureImageData[]>('capturedPhotos', 'SwissTimeEntry', false, []);
        const currentLang = await this.localStorage.get('language', 'SwissTimeEntry', true, 'de');

        let hasError = false;
        let uploadCounter = 0;
        for (let i = 0; i < indexedDBImages.length; i++) {
            const el = indexedDBImages[i];
            if (!el.synced) {
                try {
                    const data = (() => {
                        // IPad/IPhone PWA does not like uploading files as File once they hit the IndexDB,
                        // so we use data and create a Blob to upload
                        if (Capacitor.getPlatform() === 'ios' || this.platform.SAFARI) {
                            return HelperService.dataURLtoBlob(el.data) as Blob;
                        }
                        return el.blob;
                    })();

                    const result = await this.uploadImageService.uploadBild(data, el.objectID, el.fileName, el.description);
                    if (result && result.objectID) {
                        indexedDBImages![i].synced = true;
                        if (result.status != 1) {
                            uploadCounter++;
                        }
                    }
                } catch (result) {
                    hasError = true;
                    this.toasterMessagesService.toasterTranslateErrorMessages('Ein Fehler beim Bildupload ist aufgetreten! \n' + result, 'Une erreur est survenue lors du téléchargement de l\'image ! \n' + result, currentLang);
                }
            }
        }

        if (uploadCounter >= 1) {
            this.toasterMessagesService.toasterTranslateMessage(uploadCounter + ' Bild' + (uploadCounter > 1 ? 'er' : '') + ' erfolgreich hochgeladen', uploadCounter + ' Image enregistrée', currentLang);
        }
        await this.localStorage.add('capturedPhotos', indexedDBImages, 'SwissTimeEntry');

        return hasError;
    }

    async setCurrentTimeEntry(data: OrderData) {
        this.currentTimeEntry = data;
        await this.localStorage.add('currentTimeEntry', data, 'SwissTimeEntry');
    }

    public GetCostCentersByObjectID(objektNumber: string, objektStatus: number, componentData: any) {
        componentData.forEach((el: any) => this.myCostCenters.push(el.costCenterID));
        let returnModel: Promise<Kostenstelle[] | void>;
        returnModel = this.getAllCostCenterObjektRanges().then(res => {
            if (res) {
                let costCenterObjektRanges = res;
                this.myCostCentersByObjektNumber = [];
                if (costCenterObjektRanges.length === 0) {
                    this.myCostCentersByObjektNumber = this.myCostCentersByObjektNumber.concat(this.myCostCenters);
                }
                if (objektNumber.indexOf('.') >= 0) {
                    objektNumber = objektNumber.substring(0, objektNumber.indexOf('.'));
                }
                let objektIDNum: number = Number(objektNumber);
                costCenterObjektRanges.forEach((ccor: any) => {
                    this.AddCostCentersByObjektRange(ccor, objektIDNum, objektStatus);
                });
                this.kostenStelleModel = componentData.filter((costId: any) => this.myCostCentersByObjektNumber.find(el => el === costId.costCenterID));
                if (this.kostenStelleModel.length === 0) {
                    this.kostenStelleModel = componentData;
                }
            }

            return this.kostenStelleModel;
        }, err => console.error('Fehler.', err));
        return returnModel;
    }

    public async GetCostCentersByObjectID2(objektNumber: string, objektStatus: number, costCenter: Kostenstelle[]) {
        costCenter.forEach(cc => this.myCostCenters.push(cc.costCenterID));
        let ranges = await this.getAllCostCenterObjektRanges();

        if (ranges) {
            this.myCostCentersByObjektNumber = this.myCostCentersByObjektNumber.concat(this.myCostCenters);
        }

        if (objektNumber.indexOf('.') >= 0) {
            objektNumber = objektNumber.substring(0, objektNumber.indexOf('.'));
        }
        let objektIDNum: number = Number(objektNumber);

        ranges.forEach((ccor: any) => {
            this.AddCostCentersByObjektRange(ccor, objektIDNum, objektStatus);
        });
        this.kostenStelleModel = costCenter.filter(center => center.costCenterID in this.myCostCentersByObjektNumber);
        if (!this.kostenStelleModel.length) {
            this.kostenStelleModel = costCenter;
        }
        return this.kostenStelleModel;
    }


    /* Start ehemaliger "cost-center-by-object-id.service.ts" */

    public async getAllDataFromWebServices() {
        return Promise.all([this.getAllOrders(), this.getKostensStellen(), this.getAllTimeControlEntries(), this.getAllSpesen(), this.getAllEmployee(), this.setTimeEntryDeleteDays(), this.getAllCostCenterObjektRanges(), this.getPlannedEmployeeWorks(), this.getPlannedEmployeeWeeks(), this.localStorage.get('language', 'SwissTimeEntry', true, 'de')])
            .then((res) => {
                return {
                    orders: res[0],
                    costs: res[1],
                    timeEntry: res[2],
                    spesen: res[3],
                    allEmployees: res[4],
                    timeEntryDeleteDays: res[5],
                    costCenterObjektRange: res[6],
                    plannedEmployeeWorks: res[7],
                    plannedEmployeeWeeks: res[8],
                    language: res[9]
                } as IndexedDbData;
            }).catch(() => {
                return this.loadDataFromIndexDB();
            });
    }

    async hasTimeConflict(entryDate: string, startTime: Date, endTime: Date, rowID?: string): Promise<boolean> {
        const d = entryDate.split('T')[0];
        let entries: TimeControlEntry[] = (await this.localStorage.get('myTimeControlEntries', this.storage, false, []));
        entries = entries.filter((entry) => d == entry.entryDate.split('T')[0] && entry.startTime && entry.endTime);
        if (rowID) {
            entries = entries.filter(entry => entry.rowID != rowID);
        }
        const conflicts = entries.filter((entry) => {
            const sT = new Date(entry.startTime!);
            const eT = new Date(entry.endTime!);

            return (startTime > sT && startTime < eT) || (endTime < eT && endTime < sT);
        });
        return conflicts.length > 0;
    }

    private async reloadTimeControlEntries(): Promise<void> {
        // Nach dem Synchronisieren können die kontrollieren Zeiten noch abgeholt werden
        const x = await this.getAllTimeControlEntries();
        this.IndexedDbData.timeEntry = x;
    }

    private AddCostCentersByObjektRange(objektRange: CostCenterObjektRange, objektIDNum: number, objektStatus: number) {
        let from: number;
        let to: number;
        /* Range-Bereich ermitteln => nummerisch  */
        if (objektRange.objektFrom.indexOf('.') >= 0) {
            from = Number(objektRange.objektFrom.substring(0, objektRange.objektFrom.indexOf('.')));
        } else {
            from = Number(objektRange.objektFrom);
        }
        if (objektRange.objektTo.indexOf('.') >= 0) {
            to = Number(objektRange.objektTo.substring(0, objektRange.objektTo.indexOf('.')));
        } else {
            to = Number(objektRange.objektTo);
        }
        if (from <= objektIDNum && objektIDNum <= to) {
            objektRange.costCenterRangeList.forEach(costRangeItem => {
                let items: number[] = this.myCostCenters.filter(x => costRangeItem.costCenterFrom <= x && x <= costRangeItem.costCenterTo);
                if (items && items.length > 0) {
                    items.forEach(i => {
                        if (i) {
                            if (this.myCostCentersByObjektNumber.indexOf(i) === -1) {
                                this.myCostCentersByObjektNumber.push(i);
                            }
                        }
                    });
                }
            });

            if (objektRange.costCenterObjektStateList && objektRange.costCenterObjektStateList.length > 0) {
                objektRange.costCenterObjektStateList.forEach(ccos => {
                    this.AddCostCentersByState(ccos, objektStatus);
                });
            }
        }
    }

    private AddCostCentersByState(statusRange: CostCenterObjektStatusRange, objektStatus: number) {
        if (statusRange.objektStateFrom <= objektStatus && objektStatus <= statusRange.objektStateTo) {
            statusRange.costCenterStateRangeList.forEach((costRangeItem: any) => {
                let items: number[] = this.myCostCenters.filter((x: any) => costRangeItem.costCenterFrom <= x && x <= costRangeItem.costCenterTo);
                if (items && items.length > 0) {
                    items.forEach(i => {
                        if (i) {
                            if (this.myCostCentersByObjektNumber.indexOf(i) === -1) {
                                this.myCostCentersByObjektNumber.push(i);
                            }
                        }
                    });
                }
            });
        }
    }
}
